import { useEffect, useContext } from 'react';

import type { Provider, CollabEditProvider } from '@atlaskit/collab-provider';

import { COLLAB_EDIT_PROVIDER_MEASURES } from '@confluence/fabric-providers/entry-points/CollabEditProviderMeasures.ts';
import { CollabEditProviderMeasures } from '@confluence/action-measures/entry-points/CollabEditProviderMeasures.ts';
import { EDIT_PAGE_LOAD_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';

import { useNativeCollabConnected } from './useNativeCollabState.ts';
import { nativeCollabPermissionsTokenState } from './CollabPermissionsTokenState.ts';

/**
 * Listens to passed in collab provider connected event and returns isCollabProviderConnected boolean.
 */
export const useConnected = (
	collabEditProvider: (CollabEditProvider & Provider) | null,
	contentId: string,
) => {
	const [{ collabEditConnected }, nativeCollabActions] = useNativeCollabConnected();
	const experienceTracker = useContext(ExperienceTrackerContext);

	useEffect(() => {
		if (!collabEditProvider) {
			return;
		}
		const handleConnected = () => {
			nativeCollabActions.setCollabEditConnected(true);
			nativeCollabPermissionsTokenState.clearPermissionsTokenState(contentId);
			CollabEditProviderMeasures.markMeasureEnd(
				COLLAB_EDIT_PROVIDER_MEASURES.COLLAB_EDIT_PROVIDER_CONNECTED,
			);
			// tracking time between when 'init' event is fired and connection has been established
			CollabEditProviderMeasures.markMeasureEnd(
				COLLAB_EDIT_PROVIDER_MEASURES.COLLAB_EDIT_PROVIDER_INIT_DOC,
			);
			experienceTracker.attachDebugPoint({
				name: EDIT_PAGE_LOAD_EXPERIENCE,
				message: 'NCS Connected',
			});
		};
		collabEditProvider.on('connected', handleConnected);
		return () => {
			collabEditProvider.off('connected', handleConnected);
		};
	}, [collabEditProvider, nativeCollabActions, contentId, experienceTracker]);

	return { isCollabProviderConnected: collabEditConnected };
};
